import React, { createContext, useContext } from 'react';

// Theme context with CSS variables
const ThemeContext = createContext();

// Theme configuration object
export const theme = {
    colors: {
      background: 'linear-gradient(135deg, #ffffff 0%, #dcfce7 100%)',
        backgroundFooter: 'white',
        primary: '#16a34a',   // Green 600
        secondary: '#22c55e', // Green 500
        text: '#0f172a',
        subText: '#475569',
        cardBackground: 'rgba(255, 255, 255, 0.9)',
        border: 'rgba(0, 0, 0, 0.15)',
        particle: 'rgba(2, 132, 199, 0.5)',
        // Additional commonly used colors
        white: '#ffffff',
        success: '#219737',
        error: '#ff4d4f',
        warning: '#faad14',
        info: '#1890ff',
    },
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px',
    },
    borderRadius: {
        sm: '4px',
        md: '8px',
        lg: '12px',
        xl: '16px',
    },
    shadows: {
        sm: '0 1px 3px rgba(0, 0, 0, 0.12)',
        md: '0 4px 6px rgba(0, 0, 0, 0.1)',
        lg: '0 10px 25px rgba(0, 0, 0, 0.1)',
    }
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
    return (
        <ThemeContext.Provider value={theme}>
            {children}
        </ThemeContext.Provider>
    );
};

// Custom hook to use theme
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Helper function to get CSS variable value
export const getCSSVariable = (variableName) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};

// Helper function to set CSS variable
export const setCSSVariable = (variableName, value) => {
    document.documentElement.style.setProperty(variableName, value);
};

// Initialize CSS variables from theme object
export const initializeCSSVariables = () => {
    const root = document.documentElement;
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-background-footer', theme.colors.backgroundFooter);
    root.style.setProperty('--theme-primary-color', theme.colors.primary);
    root.style.setProperty('--theme-secondary-color', theme.colors.secondary);
    root.style.setProperty('--theme-text-color', theme.colors.text);
    root.style.setProperty('--theme-sub-text-color', theme.colors.subText);
    root.style.setProperty('--theme-card-background', theme.colors.cardBackground);
    root.style.setProperty('--theme-border-color', theme.colors.border);
    root.style.setProperty('--theme-particle-color', theme.colors.particle);
    
    // Additional colors
    root.style.setProperty('--theme-white', theme.colors.white);
    root.style.setProperty('--theme-success', theme.colors.success);
    root.style.setProperty('--theme-error', theme.colors.error);
    root.style.setProperty('--theme-warning', theme.colors.warning);
    root.style.setProperty('--theme-info', theme.colors.info);
    
    // Spacing
    root.style.setProperty('--theme-spacing-xs', theme.spacing.xs);
    root.style.setProperty('--theme-spacing-sm', theme.spacing.sm);
    root.style.setProperty('--theme-spacing-md', theme.spacing.md);
    root.style.setProperty('--theme-spacing-lg', theme.spacing.lg);
    root.style.setProperty('--theme-spacing-xl', theme.spacing.xl);
    root.style.setProperty('--theme-spacing-xxl', theme.spacing.xxl);
    
    // Border radius
    root.style.setProperty('--theme-border-radius-sm', theme.borderRadius.sm);
    root.style.setProperty('--theme-border-radius-md', theme.borderRadius.md);
    root.style.setProperty('--theme-border-radius-lg', theme.borderRadius.lg);
    root.style.setProperty('--theme-border-radius-xl', theme.borderRadius.xl);
    
    // Shadows
    root.style.setProperty('--theme-shadow-sm', theme.shadows.sm);
    root.style.setProperty('--theme-shadow-md', theme.shadows.md);
    root.style.setProperty('--theme-shadow-lg', theme.shadows.lg);
};

export default ThemeContext;
